import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/config";
import { SkipForward, VolumeX, Volume2 } from "lucide-react";

const SKIP_FOREVER_KEY = "fp_intro_skip_forever";
const SKIP_SESSION_KEY = "fp_intro_skip_session";

export default function IntroVideoOverlay() {
  const [meta, setMeta] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ending, setEnding] = useState(false);
  const [muted, setMuted] = useState(false); // try unmuted first
  const videoRef = useRef(null);

  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) return;
    try {
      if (localStorage.getItem(SKIP_FOREVER_KEY) === "1") return;
      if (sessionStorage.getItem(SKIP_SESSION_KEY) === "1") return;
    } catch { /* storage blocked */ }

    let alive = true;
    api.get("/intro-video/meta")
      .then((r) => {
        if (!alive) return;
        setMeta(r.data);
        if (r.data?.available && r.data?.enabled) {
          setVisible(true);
        }
      })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!visible || !videoRef.current) return;
    const v = videoRef.current;
    // Try to play unmuted first — most browsers allow this if the video is being
    // triggered right after a page load with user gesture (click). If it fails
    // due to autoplay policy, fall back to muted.
    v.muted = false;
    v.play()
      .then(() => {
        setMuted(false);
      })
      .catch(() => {
        // Autoplay with sound blocked. Fall back to muted and try again.
        v.muted = true;
        setMuted(true);
        v.play().catch(() => { /* still fails, user must interact */ });
      });
  }, [visible]);

  const closeWithFade = () => {
    setEnding(true);
    // Actual removal handled by AnimatePresence onExitComplete via visible=false after delay
    setTimeout(() => setVisible(false), 900);
  };

  const skipOnce = () => {
    try { sessionStorage.setItem(SKIP_SESSION_KEY, "1"); } catch { /* noop */ }
    closeWithFade();
  };

  const skipAlways = () => {
    try { localStorage.setItem(SKIP_FOREVER_KEY, "1"); } catch { /* noop */ }
    closeWithFade();
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  if (!visible || !meta?.available) return null;

  const streamUrl = `${api.defaults.baseURL}/intro-video/stream?v=${encodeURIComponent(meta.version || meta.uploaded_at || "")}`;

  return (
    <AnimatePresence>
      {!ending && (
        <motion.div
          key="intro-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.05, filter: "blur(20px)" }}
          transition={{
            opacity: { duration: 0.5, ease: "easeOut" },
            scale: { duration: 0.9, ease: "easeInOut" },
            filter: { duration: 0.9, ease: "easeInOut" },
          }}
          className="fixed inset-0 z-[9998] bg-black flex items-center justify-center"
          data-testid="intro-video-overlay"
        >
          <video
            ref={videoRef}
            src={streamUrl}
            className="w-full h-full object-contain bg-black"
            autoPlay
            playsInline
            controls={false}
            onEnded={closeWithFade}
            data-testid="intro-video-player"
          />

          {/* Sound toggle - top right */}
          <button
            onClick={toggleMute}
            className="absolute top-5 right-5 md:top-8 md:right-8 w-12 h-12 rounded-full bg-black/50 backdrop-blur border border-white/20 flex items-center justify-center text-white hover:bg-black/70 transition-all"
            aria-label={muted ? "Attiva audio" : "Disattiva audio"}
            data-testid="intro-mute-toggle"
          >
            {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>

          {/* Skip buttons - bottom right */}
          <div className="absolute bottom-5 right-5 md:bottom-8 md:right-8 flex flex-col sm:flex-row gap-3">
            <button
              onClick={skipAlways}
              className="px-5 py-3 rounded-2xl bg-black/50 backdrop-blur border border-white/20 text-white/80 hover:text-white hover:border-white/40 text-sm font-heading font-bold uppercase tracking-wider transition-all"
              data-testid="intro-skip-forever"
            >
              Salta sempre
            </button>
            <button
              onClick={skipOnce}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-fp-cyan text-fp-bg font-heading font-bold text-sm uppercase tracking-wider hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all"
              data-testid="intro-skip-once"
            >
              Salta <SkipForward size={16} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
